import { Controller, useFormContext } from "react-hook-form";
import { useCallback } from "react";

import FormField, { FormFieldProps } from "ds/components/Form/Field";
import FormToggleField from "ds/components/Form/ToggleField";
import Tile from "ds/components/Tile";
import Input from "ds/components/Input";
import Box from "ds/components/Box";
import Card from "ds/components/Card";

// Constants for drift detection run limit values
const DRIFT_DETECTION_UNLIMITED = -1;
const DRIFT_DETECTION_DISABLED = 0;
const DEFAULT_LIMIT_VALUE = 100;
const MAX_LIMIT_VALUE = 10000;
const MIN_LIMIT_VALUE = 1;

type FormFieldDriftDetectionRunLimitProps = Pick<
  FormFieldProps,
  "tooltipAnalyticsPage" | "tooltipAnalyticsProps" | "tooltipAnalyticsTitle" | "noMargin"
> & {
  label?: string;
};

const FormFieldDriftDetectionRunLimit = ({
  label = "Configure drift detection run limits",
  ...restProps
}: FormFieldDriftDetectionRunLimitProps) => {
  const { control, watch, setValue } = useFormContext<{ driftDetectionRunLimit: number }>();
  const driftDetectionRunLimit = watch("driftDetectionRunLimit");

  const currentValue = driftDetectionRunLimit;
  const isDriftUnlimited = currentValue !== DRIFT_DETECTION_UNLIMITED;
  const isDriftDisabled = currentValue === DRIFT_DETECTION_DISABLED;
  const limitValue = currentValue > 0 ? currentValue : DEFAULT_LIMIT_VALUE;

  const handleToggleChange = useCallback(
    (enabled: boolean) => {
      if (!enabled) {
        setValue("driftDetectionRunLimit", DRIFT_DETECTION_UNLIMITED);
      } else {
        setValue("driftDetectionRunLimit", limitValue);
      }
    },
    [setValue, limitValue]
  );

  const handleLimitChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = Number(e.target.value);

      if (!isNaN(value)) {
        const clampedValue = Math.max(MIN_LIMIT_VALUE, Math.min(MAX_LIMIT_VALUE, value));
        setValue("driftDetectionRunLimit", clampedValue);
      }
    },
    [setValue]
  );

  const handleDisableDrift = useCallback(() => {
    setValue("driftDetectionRunLimit", DRIFT_DETECTION_DISABLED);
  }, [setValue]);

  const handleSetLimit = useCallback(() => {
    setValue("driftDetectionRunLimit", limitValue);
  }, [setValue, limitValue]);

  return (
    <Controller
      name="driftDetectionRunLimit"
      control={control}
      rules={{
        validate: (value) => {
          if (value === DRIFT_DETECTION_UNLIMITED) return true; // Toggle disabled
          if (value === DRIFT_DETECTION_DISABLED) return true; // Drift detection disabled
          return Number.isInteger(value) || "Must be a positive integer";
        },
      }}
      render={({ fieldState }) => (
        <FormField noMargin={restProps.noMargin}>
          <FormToggleField
            variant="switch"
            title={label}
            description="Control how many drift detection runs can execute simultaneously on this worker pool."
            checked={isDriftUnlimited}
            onChange={handleToggleChange}
            {...restProps}
          />

          {isDriftUnlimited && (
            <Box direction="column" margin="x-large 0 0 0">
              <Card variant="secondary">
                <Box gap="large" justify="between">
                  <Tile
                    title="Disable drift detection"
                    description="Drift detection will not run on this worker pool."
                    selected={isDriftDisabled}
                    onClick={handleDisableDrift}
                    indicator="radio"
                  />
                  <Tile
                    title="Limit concurrent runs"
                    description="Set a maximum number of drift detection runs that can execute simultaneously on this worker pool."
                    selected={!isDriftDisabled}
                    onClick={handleSetLimit}
                    indicator="radio"
                  />
                </Box>

                {!isDriftDisabled && (
                  <FormField
                    label="Maximum drift detection runs"
                    helperText={`Enter a value between ${MIN_LIMIT_VALUE} and ${MAX_LIMIT_VALUE}`}
                    error={fieldState.error?.message}
                  >
                    {({ ariaInputProps }) => (
                      <Input
                        type="number"
                        max={MAX_LIMIT_VALUE}
                        min={MIN_LIMIT_VALUE}
                        value={limitValue.toString()}
                        onChange={handleLimitChange}
                        {...ariaInputProps}
                      />
                    )}
                  </FormField>
                )}
              </Card>
            </Box>
          )}
        </FormField>
      )}
    />
  );
};

export default FormFieldDriftDetectionRunLimit;
